import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, MapPin, User, Phone, VolumeX, Clock, Minus } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useSocket } from '../hooks/useSocket';
import { API_BASE_URL } from './config/api';
import InlineOrderAcceptance from './InlineOrderAcceptance';
import efoodLogo from '../assets/logos/logo_efood.png';
import woltLogo from '../assets/logos/logo_wolt.png';
import boxLogo from '../assets/logos/logo_box.png';

const platformLogos: Record<string, string> = {
  efood: efoodLogo,
  wolt: woltLogo,
  box: boxLogo,
};

// How long the alert sound rings before auto-stopping. The blocking popup stays
// visible afterwards, so a short burst is enough — no need to ring non-stop.
const ALERT_DURATION_MS = 10000;
// Κάθε πόσο συμφιλιώνεται η ουρά με τον server (δίχτυ για χαμένα socket events).
const RECONCILE_INTERVAL_MS = 30000;

interface QueueProduct {
  id: number;
  product_name: string;
  quantity: number;
  total_price: number;
  product_notes?: string | null;
}

interface DeliverySettings {
  is_self_delivery: boolean;
  has_efood_lastmile: boolean;
  has_wolt_drive: boolean;
}

/**
 * Global blocking popup for platform orders awaiting manual acceptance.
 *
 * Mounted app-wide (like IncomingCallPopup) so a new order surfaces wherever
 * the cashier is. It keeps a queue: multiple simultaneous orders are handled
 * one-by-one. Each card shows the order's products so the cashier can pick a
 * sensible prep/delivery time, then Accept/Reject. An alert sound rings for a
 * short burst per order (then auto-stops; the popup stays as the visual cue, and
 * can be silenced on demand). Auto-accept platforms are skipped (the server
 * accepts those, so they never linger as pending).
 */
const IncomingOrderQueue: React.FC = () => {
  const { t } = useTranslation(['orders', 'common']);
  const { store, platforms } = useAuth();
  const { hasPermission } = usePermissions();
  const { socket, isConnected } = useSocket();
  // Per-employee opt-out (default: everyone sees the popups). An opted-out
  // cashier can still accept from the Παραγγελίες tab — this only silences
  // the blocking popup + ringtone on their screen.
  const popupsAllowed = hasPermission('view_platform_order_popups');

  const [queue, setQueue] = useState<any[]>([]);
  const [products, setProducts] = useState<QueueProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [deliverySettings, setDeliverySettings] = useState<Record<string, DeliverySettings>>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const current = queue[0] || null;
  const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` };

  // Does this order still need a manual accept/reject decision?
  const needsManualAccept = useCallback((order: any): boolean => {
    if (!popupsAllowed) return false;
    if (!order || order.status !== 'pending') return false;
    if (!order.platform_name || order.platform_name === 'internal') return false;
    const pd = platforms?.find((p: any) => p.platform_name === order.platform_name);
    // Unknown platform → assume manual (never silently drop an order).
    return pd ? !pd.auto_accept_orders : true;
  }, [platforms, popupsAllowed]);

  const enqueue = useCallback((order: any) => {
    if (!needsManualAccept(order)) return;
    setQueue(prev => (prev.some(o => o.id === order.id) ? prev : [...prev, order]));
  }, [needsManualAccept]);

  const dequeue = useCallback((orderId: number) => {
    setQueue(prev => prev.filter(o => o.id !== orderId));
  }, []);

  // Πηγή αλήθειας για τις εκκρεμείς: ΕΝΑ HTTP ερώτημα. Το χρησιμοποιούν και οι
  // τρεις περιπτώσεις — αρχική φόρτωση, επανασύνδεση, και ο περιοδικός έλεγχος.
  const fetchPending = useCallback(async (): Promise<any[] | null> => {
    if (!store?.id) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${store.id}?status=pending&limit=50`, { headers: authHeaders });
      if (!res.ok) return null;
      const data = await res.json();
      return (data.orders || data || []).filter(needsManualAccept);
    } catch {
      return null; // το socket μπορεί ακόμα να φέρει τις νέες
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, needsManualAccept]);

  // Seed the queue with orders already pending when the app loads — and RE-seed
  // after a websocket reconnect: a platform order that arrived while the socket
  // was down never fired 'new-order', so without this it would never pop up and
  // could expire unanswered on the platform.
  const queueWasDisconnected = useRef(false);
  useEffect(() => {
    if (!isConnected) { queueWasDisconnected.current = true; }
  }, [isConnected]);
  useEffect(() => {
    if (!store?.id) return;
    // Run on mount, and again on every DOWN→UP transition.
    if (isConnected === false) return;
    const isReseed = queueWasDisconnected.current;
    queueWasDisconnected.current = false;
    let cancelled = false;
    (async () => {
      const pending = await fetchPending();
      if (cancelled || pending === null) return;
      // Merge instead of replace on reseed, so an order the cashier is
      // mid-reviewing doesn't jump position.
      setQueue(prev => {
        if (!isReseed) return pending;
        const known = new Set(prev.map((o: any) => o.id));
        return [...prev, ...pending.filter((o: any) => !known.has(o.id))];
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, fetchPending, isConnected]);

  // ΔΙΧΤΥ ΑΣΦΑΛΕΙΑΣ: περιοδικός συμφιλιωτής με τον server.
  //
  // ΓΙΑΤΙ: το popup βασιζόταν ΑΠΟΚΛΕΙΣΤΙΚΑ στο συμβάν 'new-order'. Αν αυτό χανόταν
  // (π.χ. όσο η σύνδεση ξαναχτιζόταν), η παραγγελία έμπαινε κανονικά στη βάση αλλά
  // δεν εμφανιζόταν ΠΟΤΕ στον ταμία — και η μόνη διέξοδος ήταν να κλείσει και να
  // ξανανοίξει την εφαρμογή, γιατί μόνο τότε ξαναγινόταν το HTTP ερώτημα.
  // Τώρα το ίδιο ερώτημα τρέχει μόνο του κάθε 30 δευτερόλεπτα.
  //
  // Συμφιλιώνει ΚΑΙ ΠΡΟΣ ΤΙΣ ΔΥΟ ΚΑΤΕΥΘΥΝΣΕΙΣ: προσθέτει όσες λείπουν, και αφαιρεί
  // όσες δεν είναι πια pending (π.χ. τις δέχτηκε άλλο τερματικό και χάθηκε το
  // 'order-updated'), ώστε να μη μένει κολλημένο popup για τελειωμένη παραγγελία.
  useEffect(() => {
    if (!store?.id) return;
    let cancelled = false;
    const id = setInterval(async () => {
      const pending = await fetchPending();
      if (cancelled || pending === null) return;
      setQueue(prev => {
        const live = new Set(pending.map((o: any) => o.id));
        const kept = prev.filter((o: any) => live.has(o.id));
        const known = new Set(kept.map((o: any) => o.id));
        const added = pending.filter((o: any) => !known.has(o.id));
        if (added.length === 0 && kept.length === prev.length) return prev; // καμία αλλαγή → κανένα re-render
        return [...kept, ...added];
      });
    }, RECONCILE_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [store?.id, fetchPending]);

  // Delivery method options for InlineOrderAcceptance (self / lastmile / drive).
  useEffect(() => {
    if (!store?.id || !platforms) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/stores/${store.id}/platform-flags`, { headers: authHeaders });
        const flags = res.ok ? await res.json() : {};
        if (cancelled) return;
        const settings: Record<string, DeliverySettings> = {};
        platforms.forEach((p: any) => {
          settings[p.platform_name] = {
            is_self_delivery: p.is_self_delivery || false,
            has_efood_lastmile: flags.has_efood_lastmile || false,
            has_wolt_drive: flags.has_wolt_drive || false,
          };
        });
        setDeliverySettings(settings);
      } catch { /* settings are optional */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, platforms]);

  // Listen for new orders + clear ones handled elsewhere (e.g. Orders page).
  useEffect(() => {
    if (!socket || !isConnected) return;
    const onNew = (order: any) => enqueue(order);
    const onUpdate = (data: any) => {
      const o = data?.order || data;
      const id = o?.id ?? data?.orderId;
      const status = o?.status ?? data?.status;
      if (id != null && status && status !== 'pending') dequeue(Number(id));
    };
    socket.on('new-order', onNew);
    socket.on('order-status-changed', onUpdate);
    socket.on('order-update', onUpdate);
    // The server's broadcastOrderUpdate emits 'order-updated' (note the -d). Without this listener a
    // second terminal kept showing the accept popup after another terminal accepted/rejected the order.
    socket.on('order-updated', onUpdate);
    return () => {
      socket.off('new-order', onNew);
      socket.off('order-status-changed', onUpdate);
      socket.off('order-update', onUpdate);
      socket.off('order-updated', onUpdate);
    };
  }, [socket, isConnected, enqueue, dequeue]);

  // Fetch the current order's products.
  useEffect(() => {
    if (!current || !store?.id) { setProducts([]); return; }
    let cancelled = false;
    setLoadingProducts(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/orders/${store.id}/orders/${current.id}/products`, { headers: authHeaders });
        const data = res.ok ? await res.json() : {};
        if (!cancelled) setProducts(data.products || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, store?.id]);

  // Alert sound: ring for a short burst when an order is waiting, then auto-stop.
  // The blocking popup stays as the visual reminder, so we don't ring non-stop.
  // Resets per order; can also be silenced on demand.
  const [silenced, setSilenced] = useState(false);
  // ΜΑΖΕΜΕΝΟ popup: το πλήρους οθόνης παράθυρο κλείδωνε τον ταμία έξω από ΟΛΗ την
  // εφαρμογή μέχρι να ξεμπερδέψει με κάθε παραγγελία της ριπής — δεν μπορούσε ούτε
  // πελάτη στο ταμείο να εξυπηρετήσει. Τώρα μπορεί να το μαζέψει σε μια μπάρα και
  // να επιστρέψει όποτε θέλει. Η παραγγελία ΔΕΝ χάνεται: η μπάρα μένει ορατή και
  // παλλόμενη με τον αριθμό των εκκρεμών, και ο ήχος χτυπά κανονικά.
  const [collapsed, setCollapsed] = useState(false);
  // Νέα ριπή μετά από ήσυχο διάστημα → ξαναεμφανίζεται μόνο του, ώστε να μη μείνει
  // μαζεμένο για πάντα από ένα κλικ που έγινε πριν από ώρα.
  useEffect(() => { if (!current) setCollapsed(false); }, [current]);
  // New order at the front of the queue → allow it to ring again.
  useEffect(() => { setSilenced(false); }, [current?.id]);

  useEffect(() => {
    const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
    // popupsAllowed can flip mid-session (PIN switch to an opted-out cashier)
    // while items are already queued — stop ringing for them too.
    if (!current || silenced || !popupsAllowed) { stop(); return; }
    try {
      const audio = new Audio('/notification.mp3');
      audio.loop = true;
      audio.play().catch(() => {});
      audioRef.current = audio;
    } catch { /* no audio, fine */ }
    const timer = setTimeout(stop, ALERT_DURATION_MS);
    return () => { clearTimeout(timer); stop(); };
  }, [current?.id, silenced, popupsAllowed]);

  if (!current || !popupsAllowed) return null;

  const od = (current.order_json || {}) as any;
  // The customer lives in different places per platform: the normalized order columns (populated for
  // every platform at ingestion), od.customer (Wolt / Box / e-food test format), or
  // od.orders[0].customer (the REAL e-food webhook payload, which is { orders: [{ customer }] }).
  // Prefer the normalized columns and fall back through the raw shapes so e-food shows up too.
  const ojCustomer = od?.customer || od?.orders?.[0]?.customer || {};
  const customerName = (current.customer_name && String(current.customer_name).trim())
    || (ojCustomer.name ? `${ojCustomer.name} ${ojCustomer.surname || ''}`.trim() : '')
    || t('unknownCustomer');
  const address = current.delivery_address || ojCustomer.address || '';
  const phone = current.customer_phone || ojCustomer.telephone || ojCustomer.phone || '';
  const isTakeaway = current.order_type === 'pickup' || current.order_type === 'takeaway';
  const platformData = platforms?.find((p: any) => p.platform_name === current.platform_name);
  const total = Number(current.total_amount || 0);

  const handleAccept = async (deliveryTime: number, deliveryMethod?: string) => {
    const res = await fetch(`${API_BASE_URL}/api/orders/${current.id}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ estimatedTime: deliveryTime, deliveryMethod }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const err: any = new Error(e.message || t('common:errors.acceptOrder'));
      err.code = e.code;
      err.status = res.status;
      err.dismissible = e.dismissible;
      throw err;
    }
    dequeue(current.id);
  };

  // Local dismiss — resolve a stuck platform order on our side so the blocking
  // popup clears. Persists (backend marks it cancelled), unlike the plain
  // dequeue in onStale which only hides it until the next reload.
  const handleDismiss = async (reason: string) => {
    const res = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/orders/${current.id}/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.message || t('common:errors.dismissOrder'));
    }
    dequeue(current.id);
  };

  const handleReject = async (reason?: string) => {
    const res = await fetch(`${API_BASE_URL}/api/orders/${current.id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ reason: reason || 'Rejected by store' }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.message || t('common:errors.rejectOrder'));
    }
    dequeue(current.id);
  };

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed bottom-4 right-4 z-[100] flex items-center gap-3 px-4 py-3 rounded-2xl
                   bg-amber-500 text-slate-900 shadow-2xl shadow-amber-500/30 border-2 border-amber-400
                   animate-pulse hover:animate-none active:scale-95 transition-transform"
      >
        <Clock className="w-5 h-5 shrink-0" />
        <span className="font-bold">
          {queue.length} {queue.length === 1 ? t('orderWaiting', { defaultValue: 'παραγγελία σε αναμονή' })
                                             : t('ordersWaiting', { defaultValue: 'παραγγελίες σε αναμονή' })}
        </span>
        <span className="text-xs font-semibold opacity-80">
          {t('tapToOpen', { defaultValue: 'πατήστε για άνοιγμα' })}
        </span>
      </button>
    );
  }

  return (
    <div className="cashier-overlay fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3">
      <div className="cashier-dialog bg-surface-1 rounded-2xl border-2 border-amber-500/50 w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl shadow-amber-500/10">
        {/* Header */}
        <div className="cashier-dialog-header px-5 py-3.5 border-b border-line flex items-center gap-3 shrink-0">
          <img
            src={platformLogos[current.platform_name] || efoodLogo}
            alt={current.platform_name}
            className="w-9 h-9 object-contain"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-t1">
                #{current.short_code || current.platform_order_id?.slice(-6)}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase bg-amber-500 text-slate-900 animate-pulse">
                {t('awaitingAcceptance')}
              </span>
            </div>
            {queue.length > 1 && (
              <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                +{queue.length - 1} {t('moreWaiting')}
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-t1">{formatMoney(total)}</div>
            <div className="text-[11px] text-t3">
              {current.payment_type === 'cash' ? t('common:payment.cash')
                : ['card', 'pos'].includes(current.payment_type) ? t('common:payment.card')
                : t('common:payment.online')}
            </div>
          </div>
          {/* Silence the alert sound (the popup stays — the order still needs a
              decision, so we never let it be dismissed and forgotten). */}
          <button
            onClick={() => setCollapsed(true)}
            title={t('collapsePopup', { defaultValue: 'Μάζεμα — συνεχίστε να δουλεύετε' })}
            className="p-2 rounded-lg text-t3 hover:text-t1 hover:bg-surface-3 transition-colors shrink-0"
          >
            <Minus className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSilenced(true)}
            disabled={silenced}
            title={t('silenceAlert', { defaultValue: 'Σίγαση ήχου' })}
            aria-label={t('silenceAlert', { defaultValue: 'Σίγαση ήχου' })}
            className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-t3 hover:text-t1 hover:bg-surface-2 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <VolumeX className="h-5 w-5" />
          </button>
        </div>

        {/* Customer */}
        <div className="px-5 py-3 border-b border-line/60 space-y-1.5 shrink-0">
          <div className="flex items-center gap-2 text-sm text-t1 font-medium">
            <User className="h-3.5 w-3.5 text-t4" /> {customerName}
            {isTakeaway && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">TAKE AWAY</span>
            )}
          </div>
          {!isTakeaway && address && (
            <div className="flex items-start gap-2 text-sm text-t2">
              <MapPin className="h-3.5 w-3.5 text-t4 mt-0.5" /> {address}
            </div>
          )}
          {phone && (
            <div className="flex items-center gap-2 text-sm text-t2">
              <Phone className="h-3.5 w-3.5 text-t4" /> {phone}
            </div>
          )}
        </div>

        {/* Products */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-t3 uppercase tracking-wider">
            <ShoppingBag className="h-3.5 w-3.5" /> {t('products')}
          </div>
          {loadingProducts ? (
            <p className="text-sm text-t4 py-2">…</p>
          ) : products.length === 0 ? (
            <p className="text-sm text-t4 py-2">{t('noOrderProducts')}</p>
          ) : (
            <div className="space-y-2">
              {products.map((p, i) => (
                <div key={p.id ?? i} className="flex items-start justify-between gap-3 bg-surface-2 dark:bg-surface-2/40 rounded-lg px-3 py-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="font-bold text-amber-700 dark:text-amber-400 text-sm shrink-0">{p.quantity}×</span>
                    <div className="min-w-0">
                      <p className="text-sm text-t1 font-medium">{p.product_name}</p>
                      {p.product_notes && (
                        <p className="text-xs text-t3 whitespace-pre-line">{p.product_notes}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-t2 shrink-0">{formatMoney(Number(p.total_price || 0))}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Accept / Reject controls (reused) */}
        <div className="px-5 py-4 border-t border-line shrink-0">
          {/* key: ΝΕΟ στιγμιότυπο ανά παραγγελία. Χωρίς αυτό το React κρατούσε την
              κατάσταση της προηγούμενης — μετά την αποδοχή της 1ης (dequeue + μετά
              setShowControls(false)) η 2η εμφανιζόταν ΧΩΡΙΣ χρόνους αποδοχής μέχρι
              να ξανανοίξει η εφαρμογή (Κουλουράδες, efood σε ριπή, 1/10/2026). */}
          <InlineOrderAcceptance
            key={current.id}
            order={current}
            presetTimes={platformData?.preset_times}
            storeDeliverySettings={deliverySettings[current.platform_name]}
            onAccept={handleAccept}
            onDismiss={handleDismiss}
            onReject={handleReject}
            onStale={() => dequeue(current.id)}
            isTakeaway={isTakeaway}
          />
        </div>
      </div>
    </div>
  );
};

export default IncomingOrderQueue;
